import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { KTCard, KTCardBody } from '../../../../../_metronic/helpers';
import { SHOPS_PAGE_SIZE, useGetShops } from '../api/shops.queries';
import {
	SUBSCRIPTION_STATUS_LABELS,
	Shop,
	ShopStateFilter,
	formatDate,
	money,
	shopState,
} from '../model/types';
import { ShopsPagination } from './ShopsPagination';

const FILTERS: { value: ShopStateFilter | null; label: string }[] = [
	{ value: null, label: 'Все' },
	{ value: 'active', label: 'Включённые' },
	{ value: 'inactive', label: 'Выключенные' },
	// Отдельно от «выключенных»: такой магазин числится включённым, и по
	// флагу вкл/выкл его не найти, а продавец о поломке может не знать.
	{ value: 'problem', label: 'Проблемные' },
];

const BotName = ({ shop }: { shop: Shop }) => {
	const username = shop.bot_name.replace(/^@/, '');
	if (!username) return <span className='text-muted'>—</span>;

	return (
		<a
			href={`https://t.me/${username}`}
			target='_blank'
			rel='noreferrer'
			className='text-muted fs-7 text-hover-primary'
		>
			@{username}
		</a>
	);
};

const SubscriptionCell = ({ shop }: { shop: Shop }) => {
	const subscription = shop.subscription;
	if (!subscription) return <span className='text-muted'>—</span>;

	return (
		<div className='d-flex flex-column'>
			<span>{subscription.subscription?.name ?? 'Тариф удалён'}</span>
			<span className='text-muted fs-8'>
				{SUBSCRIPTION_STATUS_LABELS[subscription.status] ??
					subscription.status}
				{' · до '}
				{formatDate(subscription.end_date)}
				{/* Следующий период уже куплен — иначе дата читается как
				    «скоро кончится», хотя не кончится. */}
				{shop.next_subscription && ' · оплачен вперёд'}
			</span>
		</div>
	);
};

/**
 * Все магазины платформы.
 *
 * До этого экрана магазины в платформенной админке не существовали: их
 * заводит продавец у себя, а оператор мог только забанить владельца —
 * и то не видя, что у того за магазин и торгует ли он вообще.
 */
export const ShopsPage = () => {
	const [search, setSearch] = useState('');
	const [name, setName] = useState('');
	const [state, setState] = useState<ShopStateFilter | null>(null);
	const [page, setPage] = useState(1);

	// Поиск с задержкой: запрос на каждую букву перегружает и базу, и
	// глаза — таблица дёргалась бы на каждом нажатии.
	useEffect(() => {
		const timer = setTimeout(() => {
			setName(search.trim());
			setPage(1);
		}, 400);
		return () => clearTimeout(timer);
	}, [search]);

	const { data, isLoading, isError } = useGetShops({ name, state, page });

	const shops = data?.data ?? [];
	const total = data?.total ?? 0;

	return (
		<KTCard className='w-100'>
			<KTCardBody className='py-4'>
				<div className='d-flex flex-stack flex-wrap gap-3 mb-6'>
					<div className='d-flex align-items-center gap-3'>
						<h3 className='fw-bold m-0'>Магазины</h3>
						{!isLoading && (
							<span className='text-muted fs-7'>всего {total}</span>
						)}
					</div>

					<div className='d-flex align-items-center gap-3'>
						<input
							type='text'
							className='form-control form-control-sm w-300px'
							placeholder='Название, @бот или telegram_id владельца'
							value={search}
							onChange={e => setSearch(e.target.value)}
						/>
						<div className='btn-group'>
							{FILTERS.map(filter => (
								<button
									key={String(filter.value)}
									type='button'
									className={`btn btn-sm ${
										state === filter.value ? 'btn-primary' : 'btn-light'
									}`}
									onClick={() => {
										setState(filter.value);
										setPage(1);
									}}
								>
									{filter.label}
								</button>
							))}
						</div>
					</div>
				</div>

				{isError ? (
					<div className='text-center text-danger py-10'>
						Не удалось загрузить магазины
					</div>
				) : isLoading ? (
					<div className='d-flex justify-content-center py-10'>
						<span className='spinner-border text-primary' role='status' />
					</div>
				) : shops.length === 0 ? (
					<div className='text-center text-muted py-10'>
						{name || state !== null
							? 'Под фильтр ничего не подошло'
							: 'Магазинов пока нет'}
					</div>
				) : (
					<div className='table-responsive'>
						<table className='table align-middle table-row-dashed fs-6 gy-4'>
							<thead>
								<tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
									<th>Магазин</th>
									<th>Состояние</th>
									<th>Владелец</th>
									<th>Тариф</th>
									<th className='text-end'>Товары</th>
									<th className='text-end'>Сделки</th>
									<th className='text-end'>Продано</th>
									<th className='text-end'>Создан</th>
								</tr>
							</thead>
							<tbody className='fw-semibold text-gray-600'>
								{shops.map(shop => {
									// Не `state`: снаружи так зовётся выбранный фильтр,
									// и одно имя на два разных смысла читается плохо.
									const rowState = shopState(shop);
									return (
										<tr key={shop.uuid}>
											<td>
												<div className='d-flex flex-column'>
													<Link
														to={`/apps/shops/${shop.uuid}`}
														className='text-gray-800 text-hover-primary fw-bold'
													>
														{shop.name}
													</Link>
													<BotName shop={shop} />
												</div>
											</td>
											<td>
												<span className={`badge ${rowState.badge}`}>
													{rowState.label}
												</span>
											</td>
											<td>
												{shop.owner ? (
													<Link
														to={`/apps/profile/overview/${shop.owner_uuid}`}
														className='text-hover-primary'
													>
														{shop.owner.telegram_id}
													</Link>
												) : (
													<span className='text-muted'>—</span>
												)}
											</td>
											<td>
												<SubscriptionCell shop={shop} />
											</td>
											<td className='text-end'>
												{shop.stats.products_active}
												<span className='text-muted'>
													{' / '}
													{shop.stats.products_total}
												</span>
											</td>
											<td className='text-end'>
												{shop.stats.deals_finished}
												<span className='text-muted'>
													{' / '}
													{shop.stats.deals_total}
												</span>
											</td>
											<td className='text-end'>
												{money(shop.stats.sold_cents)}
											</td>
											<td className='text-end'>
												{formatDate(shop.created_at)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>

						<ShopsPagination
							page={page}
							total={total}
							pageSize={SHOPS_PAGE_SIZE}
							onChange={setPage}
						/>
					</div>
				)}
			</KTCardBody>
		</KTCard>
	);
};
